// Sample opportunity that would drive recruitment
// In production, this comes from the backend per-campaign
export const SAMPLE_OPPORTUNITY = {
  id: 'cf-study-1',
  condition: 'Cystic Fibrosis',
  conditionShort: 'CF',
  type: 'Interview',
  title: '60-Minute Interview on Living with CF',
  pay: '$300',
  duration: '60 min',
  sponsor: 'a pharmaceutical company developing cystic fibrosis therapies',
  description: 'Share your experience managing cystic fibrosis with a research team developing next-generation therapies.',
  requirements: [
    'Diagnosed with cystic fibrosis',
    '18 years or older',
    'US resident',
    'Currently receiving treatment'
  ]
};

// Screener questions — condition-specific qualifying questions
export const SCREENER_QUESTIONS = {
  'Cystic Fibrosis': [
    { id: 'cf-dx', question: 'Have you been diagnosed with cystic fibrosis by a physician?', type: 'yesno', required: true, disqualifyOn: 'No' },
    { id: 'cf-age', question: 'What age were you diagnosed?', type: 'select', options: ['Birth – 2 years', '3 – 12 years', '13 – 17 years', '18+ years'], required: true },
    { id: 'cf-treatment', question: 'Are you currently taking any CFTR modulator therapy (e.g., Trikafta, Symdeko, Orkambi)?', type: 'yesno', required: true },
    { id: 'cf-hospitalization', question: 'How many times have you been hospitalized for CF-related issues in the past 12 months?', type: 'select', options: ['0', '1–2', '3–5', 'More than 5'], required: true },
    { id: 'cf-trial', question: 'Have you ever participated in a clinical trial?', type: 'yesno', required: false }
  ],
  'Lupus': [
    { id: 'lupus-dx', question: 'Have you been diagnosed with lupus (SLE) by a rheumatologist?', type: 'yesno', required: true, disqualifyOn: 'No' },
    { id: 'lupus-years', question: 'How long have you been living with lupus?', type: 'select', options: ['Less than 1 year', '1–3 years', '3–5 years', '5–10 years', '10+ years'], required: true },
    { id: 'lupus-meds', question: 'Are you currently taking medication for lupus?', type: 'yesno', required: true },
    { id: 'lupus-flares', question: 'How often do you experience flares?', type: 'select', options: ['Rarely', 'A few times a year', 'Monthly', 'Weekly or more'], required: true }
  ],
  'Narcolepsy': [
    { id: 'narc-dx', question: 'Have you been diagnosed with narcolepsy by a sleep specialist or neurologist?', type: 'yesno', required: true, disqualifyOn: 'No' },
    { id: 'narc-type', question: 'What type of narcolepsy were you diagnosed with?', type: 'select', options: ['Type 1 (with cataplexy)', 'Type 2 (without cataplexy)', 'Not sure'], required: true },
    { id: 'narc-meds', question: 'Are you currently taking medication for narcolepsy?', type: 'yesno', required: true },
    { id: 'narc-impact', question: 'How much does narcolepsy affect your daily activities?', type: 'select', options: ['Minimally', 'Moderately', 'Significantly', 'Severely'], required: true }
  ],
  'Polycythemia Vera': [
    { id: 'pv-dx', question: 'Have you been diagnosed with polycythemia vera by a hematologist?', type: 'yesno', required: true, disqualifyOn: 'No' },
    { id: 'pv-years', question: 'How long since your diagnosis?', type: 'select', options: ['Less than 1 year', '1–3 years', '3–5 years', '5+ years'], required: true },
    { id: 'pv-phlebotomy', question: 'Do you currently receive phlebotomy treatments?', type: 'yesno', required: true },
    { id: 'pv-meds', question: 'Are you on any cytoreductive therapy (e.g., hydroxyurea, ruxolitinib)?', type: 'yesno', required: true }
  ],
  'IBD': [
    { id: 'ibd-dx', question: 'Have you been diagnosed with IBD (Crohn\'s or ulcerative colitis)?', type: 'yesno', required: true, disqualifyOn: 'No' },
    { id: 'ibd-type', question: 'Which type of IBD?', type: 'select', options: ['Crohn\'s disease', 'Ulcerative colitis', 'Indeterminate colitis'], required: true },
    { id: 'ibd-years', question: 'How long since your diagnosis?', type: 'select', options: ['Less than 1 year', '1–3 years', '3–5 years', '5–10 years', '10+ years'], required: true },
    { id: 'ibd-meds', question: 'Are you currently on biologic therapy?', type: 'yesno', required: true }
  ],
  'Cancer': [
    { id: 'cancer-dx', question: 'Have you been diagnosed with cancer?', type: 'yesno', required: true, disqualifyOn: 'No' },
    { id: 'cancer-type', question: 'What type of cancer?', type: 'select', options: ['Breast', 'Lung', 'Prostate', 'Colorectal', 'Other'], required: true },
    { id: 'cancer-stage', question: 'What is your current status?', type: 'select', options: ['In active treatment', 'In remission', 'Survivorship (5+ years)', 'Other'], required: true },
    { id: 'cancer-trial', question: 'Have you participated in a clinical trial?', type: 'yesno', required: false }
  ],
  'Asthma': [
    { id: 'asthma-dx', question: 'Have you been diagnosed with asthma by a physician?', type: 'yesno', required: true, disqualifyOn: 'No' },
    { id: 'asthma-severity', question: 'How would you describe your asthma severity?', type: 'select', options: ['Mild intermittent', 'Mild persistent', 'Moderate persistent', 'Severe persistent'], required: true },
    { id: 'asthma-inhaler', question: 'Do you use a daily controller inhaler?', type: 'yesno', required: true },
    { id: 'asthma-er', question: 'How many ER visits for asthma in the past year?', type: 'select', options: ['0', '1–2', '3+'], required: true }
  ],
  'MDD': [
    { id: 'mdd-dx', question: 'Have you been diagnosed with major depressive disorder?', type: 'yesno', required: true, disqualifyOn: 'No' },
    { id: 'mdd-years', question: 'How long have you been managing depression?', type: 'select', options: ['Less than 1 year', '1–3 years', '3–5 years', '5+ years'], required: true },
    { id: 'mdd-treatment', question: 'Are you currently receiving treatment (medication and/or therapy)?', type: 'yesno', required: true },
    { id: 'mdd-meds-tried', question: 'How many antidepressants have you tried?', type: 'select', options: ['1', '2–3', '4+', 'None'], required: true }
  ],
  'Allergy': [
    { id: 'allergy-dx', question: 'Have you been diagnosed with allergies by an allergist?', type: 'yesno', required: true, disqualifyOn: 'No' },
    { id: 'allergy-type', question: 'What type of allergies do you have?', type: 'select', options: ['Food allergies', 'Environmental/seasonal', 'Drug allergies', 'Multiple types'], required: true },
    { id: 'allergy-treatment', question: 'Are you currently on immunotherapy (allergy shots or drops)?', type: 'yesno', required: true },
    { id: 'allergy-impact', question: 'How much do your allergies impact daily life?', type: 'select', options: ['Minimally', 'Moderately', 'Significantly', 'Severely'], required: true }
  ]
};

// Seed Reflect question for new condition panels
export const SEED_QUESTIONS = {
  'Cystic Fibrosis': [{ id: 'cf-q1', question: 'What do you wish people understood about living with cystic fibrosis?' }],
  'Lupus': [{ id: 'opp-lupus-q1', question: 'What has been the hardest part of your lupus treatment journey?' }],
  'Narcolepsy': [{ id: 'opp-narc-q1', question: 'What do you wish people understood about living with narcolepsy?' }],
  'Polycythemia Vera': [{ id: 'opp-pv-q1', question: 'What surprised you most about living with polycythemia vera?' }],
  'IBD': [{ id: 'opp-ibd-q1', question: 'What does managing IBD look like on a typical day for you?' }],
  'Cancer': [{ id: 'opp-cancer-q1', question: 'What do you wish you had known at the start of your cancer journey?' }],
  'Asthma': [{ id: 'opp-asthma-q1', question: 'What triggers or situations worry you most about your asthma?' }],
  'MDD': [{ id: 'opp-mdd-q1', question: 'What has helped you most in managing depression — and what hasn\'t?' }],
  'Allergy': [{ id: 'opp-allergy-q1', question: 'How do your allergies affect decisions most people take for granted?' }]
};

// Seed answers (for first patients to see something)
export const SEED_ANSWERS = {
  'cf-q1': [
    {
      id: 1,
      text: "It's not just a lung disease. It affects my digestion, my energy, my mental health — everything. People see me coughing and think that's the whole story.",
      votes: 34,
      author: 'Sarah M.',
      diagnosisTime: 'Since birth'
    },
    {
      id: 2,
      text: "The daily treatment burden is invisible. I spend 2-3 hours a day on airway clearance, nebulizers, and medications before I even start my actual day.",
      votes: 28,
      author: 'James K.',
      diagnosisTime: 'Since birth'
    },
    {
      id: 3,
      text: "Planning ahead is a luxury. I never know if I'll have a good week or end up in the hospital. People don't get why I'm hesitant to commit to things.",
      votes: 22,
      author: 'Maya R.',
      diagnosisTime: '3-12 years'
    }
  ]
};
